import type {
  ClearQuestionnaireAnswerInput,
  CompleteQuestionnaireInput,
  RecordQuestionnaireAnswerInput,
} from "@repo/api/schemas";
import type { PatientDocumentId, QuestionnaireInstanceId } from "@repo/domain";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { authenticatedFetch, edge, queryClient } from "./client";
import { EDGE_URL, session } from "./session";

export type {
  ConsentFormDto,
  PatientDocumentDto,
  PatientQuestionnaireDto,
  QuestionnaireInstanceDto,
  VisitDto,
} from "@repo/edge/types";

// No push channel: flows and staff assign questionnaires asynchronously, so the list is polled.
const ASSIGNED_WORK_POLL_MS = 1_000;

export function usePatientQuestionnaires(params: { patientId?: string }) {
  return (
    useQuery({
      queryKey: ["portal", "questionnaires", params.patientId],
      queryFn: ({ signal }) =>
        edge.questionnaires.query(undefined, { signal, context: { patientId: params.patientId } }),
      enabled: !!params.patientId,
      refetchInterval: ASSIGNED_WORK_POLL_MS,
    }).data ?? []
  );
}
export function useQuestionnaireInstance(id: QuestionnaireInstanceId | undefined) {
  const patientId = session.getProfile()?.selectedPatientId;
  const query = useQuery({
    queryKey: ["portal", "questionnaire", patientId, id],
    queryFn: ({ signal }) => edge.questionnaire.query({ id: id ?? "" }, { signal, context: { patientId } }),
    enabled: !!id,
  });
  return { instance: query.data, isLoading: query.isLoading };
}
export function useVisits(params: { patientId?: string }) {
  const query = useQuery({
    queryKey: ["portal", "appointments", params.patientId],
    queryFn: ({ signal }) => edge.appointments.query(undefined, { signal, context: { patientId: params.patientId } }),
    enabled: !!params.patientId,
  });
  return { visits: query.data ?? [], isLoading: query.isLoading, isError: query.isError };
}
export function usePatientDocuments(params: { patientId?: string }) {
  const query = useQuery({
    queryKey: ["portal", "documents", params.patientId],
    queryFn: ({ signal }) => edge.documents.query(undefined, { signal, context: { patientId: params.patientId } }),
    enabled: !!params.patientId,
  });
  return { documents: query.data ?? [], isLoading: query.isLoading, isReady: query.isSuccess, isError: query.isError };
}
export const consentFormsQuery = (patientId: string | undefined) => ({
  queryKey: ["portal", "consents", patientId],
  queryFn: () => edge.consentForms.query(),
});
export const consentStatusQuery = (patientId: string | undefined) => ({
  queryKey: ["portal", "consent-status", patientId],
  queryFn: () => edge.consentStatus.query(),
});
export function useConsentStatus(params: { patientId?: string }) {
  const query = useQuery({ ...consentStatusQuery(params.patientId), enabled: !!params.patientId });
  return { hasConsented: query.data?.hasConsented === true, isPending: query.isPending, isError: query.isError };
}
export function useConsentForms(params: { patientId?: string }) {
  const query = useQuery({ ...consentFormsQuery(params.patientId), enabled: !!params.patientId });
  return { forms: query.data ?? [], isPending: query.isPending, isError: query.isError };
}
/**
 * `awaitRefresh: false` returns as soon as the write lands and refreshes in the
 * background: answering a question must not wait on the questionnaires list,
 * which takes about a second to read.
 */
async function write<T>(operation: () => Promise<T>, { awaitRefresh = true } = {}): Promise<T> {
  try {
    const result = await operation();
    const refresh = queryClient.invalidateQueries({ queryKey: ["portal"] });
    if (awaitRefresh) await refresh;
    return result;
  } catch (error) {
    toast.error(error instanceof Error ? error.message : "Could not save. Please try again.");
    throw error;
  }
}
export const recordQuestionnaireAnswer = (input: RecordQuestionnaireAnswerInput) =>
  write(() => edge.recordAnswer.mutate(input), { awaitRefresh: false });
export const clearQuestionnaireAnswer = (input: ClearQuestionnaireAnswerInput) =>
  write(() => edge.clearAnswer.mutate(input), { awaitRefresh: false });
export const completeQuestionnaire = (input: CompleteQuestionnaireInput) =>
  write(() => edge.completeQuestionnaire.mutate(input));
export interface ConsentAnswerInput {
  questionId: string;
  occurrence?: number;
  value: RecordQuestionnaireAnswerInput["value"];
}
export const signConsent = (input: {
  form: { url: string; version: string };
  signedName: string;
  answers: ConsentAnswerInput[];
}) => write(() => edge.signConsent.mutate(input));
export const retractPatientDocument = (id: PatientDocumentId) => write(() => edge.retractDocument.mutate({ id }));
export const uploadPatientRecord = (file: File) => {
  const form = new FormData();
  form.set("file", file);
  return write(() => edge.uploadDocument.mutate(form));
};
export async function downloadPatientDocument(document: { id: string; title: string }) {
  const { url } = await edge.documentLink.query({ id: document.id });
  // Same-tab navigation works after an await and does not require CORS or a popup.
  // Storage decides Content-Disposition; cross-origin `download` cannot rename it.
  window.location.assign(url);
}
/** Through edge, not the signed link: storage CORS admits only Medplum's app, so the portal cannot read it. */
export async function fetchPatientDocumentBytes(id: string): Promise<Blob> {
  const token = await session.accessToken();
  const patientId = session.getProfile()?.selectedPatientId;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (patientId) headers["X-Patient-Id"] = patientId;
  const response = await authenticatedFetch(`${EDGE_URL}/documents/${encodeURIComponent(id)}`, { headers });
  if (!response.ok) throw new Error(`Document fetch failed: ${response.status}`);
  return response.blob();
}
